import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LinkedAccountRepresentation,
  getLinkedAccounts,
  unLinkAccount,
  useEnvironment,
} from '@keycloak/keycloak-account-ui';
import { Apple, Link, LinkOff } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { Button } from './ui/Button';
import { px2rem } from './utils/px2rem';

export default function LinkedAccounts() {
  const { t } = useTranslation('linkedAccounts');
  const context = useEnvironment();
  const { login } = context.keycloak;

  const [unlinkedAccounts, setUnlinkedAccounts] = useState<
    LinkedAccountRepresentation[]
  >([]);

  const [linkedAccounts, setLinkedAccounts] = useState<
    LinkedAccountRepresentation[]
  >([]);

  const [, setLoading] = useState(false);

  const getAccounts = () => {
    setLoading(true);
    getLinkedAccounts({ context }, { linked: true, first: 0, max: 6 })
      .then((accounts) => setLinkedAccounts(accounts))
      .finally(() => setLoading(false));

    getLinkedAccounts({ context }, { linked: false, first: 0, max: 6 })
      .then((accounts) => setUnlinkedAccounts(accounts))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getAccounts();
  }, []);

  const getIcon = (provider: string) => {
    switch (provider) {
      case 'apple':
        return <Apple />;
      case 'google':
        return (
          <img
            style={{ width: 24, height: 24 }}
            src={`${context.environment.resourceUrl}/google.png`}
          />
        );
      case 'facebook':
        return (
          <img
            style={{ width: 24, height: 24 }}
            src={`${context.environment.resourceUrl}/facebook.png`}
          />
        );
    }
  };

  return (
    <>
      <Typography
        fontWeight={{ xs: 800, sm: 400 }}
        fontSize={{ xs: 24, sm: 36 }}
        variant="h4"
        sx={{ marginBottom: 6 }}
      >
        {t('title')}
      </Typography>
      <Typography
        sx={{ fontSize: px2rem(14), marginBottom: 3, maxWidth: 542 }}
        color="secondary"
      >
        {t('subtitle')}
      </Typography>
      {linkedAccounts.length > 0 && (
        <Typography fontSize={{ sx: 16, sm: 22 }} mb={2}>
          {t('linkedTitle')}
        </Typography>
      )}
      {linkedAccounts.map((account) => (
        <Box
          key={account.providerName}
          sx={{
            outline: (theme) => `solid 2px ${theme.palette.success.main}`,
            borderRadius: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 2,
            marginBottom: 1,
          }}
        >
          <Box sx={{ display: 'flex' }}>
            {getIcon(account.providerName)}
            <Typography
              sx={{
                marginLeft: 2,
                textTransform: 'capitalize',
                fontWeight: 500,
              }}
            >
              {account.providerAlias}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            sx={{
              height: 40,
              paddingRight: 3,
              paddingLeft: 2,
              fontWeight: 700,
            }}
            onClick={() =>
              unLinkAccount(context, account).then(() => {
                getAccounts();
              })
            }
          >
            <LinkOff sx={{ width: 18, height: 18, marginRight: 1 }} />
            {t('unlinkButton')}
          </Button>
        </Box>
      ))}
      {unlinkedAccounts.length > 0 && (
        <Typography fontSize={{ sx: 16, sm: 22 }} mb={2.25} mt={4.25}>
          {t('unlinkedTitle')}
        </Typography>
      )}
      {unlinkedAccounts.map((account) => (
        <Box
          key={account.providerName}
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            borderRadius: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            padding: 2,
            marginBottom: 1,
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box sx={{ display: 'flex' }}>
            {getIcon(account.providerName.toLowerCase())}
            <Typography
              sx={{
                marginLeft: 2,
                textTransform: 'capitalize',
                fontWeight: 500,
              }}
            >
              {account.providerAlias}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            sx={{
              height: 40,
              width: { xs: '100%', sm: 'initial' },
              paddingRight: 3,
              paddingLeft: 2,
              fontWeight: 700,
            }}
            onClick={() => {
              login({
                action: `idp_link:${account.providerAlias}`,
              });
            }}
          >
            <Link sx={{ width: 18, height: 18, marginRight: 1 }} />
            {t('linkButton')}
          </Button>
        </Box>
      ))}
    </>
  );
}
